import type { ComponentType, ReactNode } from "react";
import type * as THREE from "three";
import { ThemeTokens, type ThemeTokensInput } from "@cfg/theme";
import { z } from "zod";

/* -------------------------------------------------------------------------- */
/* Serializable brand config: lives in brand.config.ts and is emitted as       */
/* brand.json at deploy so tokens/copy can change without a rebuild.          */
/* -------------------------------------------------------------------------- */

export const BrandFeatures = z.object({
  saveConfigs: z.boolean().default(true),
  shareLinks: z.boolean().default(true),
  export3mf: z.boolean().default(true),
  exportStl: z.boolean().default(true),
  floatingLabels: z.boolean().default(true),
  analytics: z.boolean().default(true),
  /** Show designs published to the `beta` channel for signed-in staff. */
  betaChannel: z.boolean().default(false),
  /** Let users tag saved configurations with what they fit (see `fitTags`). */
  fitTags: z.boolean().default(false),
  /** Community gallery: users publish configurations, everyone browses them. */
  publicGallery: z.boolean().default(false),
});
export type BrandFeatures = z.infer<typeof BrandFeatures>;

export const BrandCatalog = z.object({
  /** Design ids or globs ("brickbrackets.*") to include; empty = every design in the listed packs. */
  include: z.array(z.string()).default([]),
  exclude: z.array(z.string()).default([]),
  /** Only designs carrying at least one of these tags. */
  tags: z.array(z.string()).default([]),
});

/** How a brand names and validates fit tags. The core only knows "something this fits". */
export const BrandFitTags = z.object({
  /** Noun for one tag, e.g. "LEGO® set". */
  label: z.string().default("Fits"),
  /** Stricter client-side check on the ref, e.g. "^\d{3,7}(-\d{1,2})?$". The API only enforces a generic shape. */
  refPattern: z.string().optional(),
  refPlaceholder: z.string().default(""),
  namePlaceholder: z.string().default("Name (optional)"),
  /** Shown in the footer and on community pages, e.g. a trademark notice. */
  disclaimer: z.string().optional(),
});
export type BrandFitTags = z.infer<typeof BrandFitTags>;

export const BrandConfig = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string(),
  tagline: z.string().default(""),
  domain: z.string().optional(),
  locale: z.string().default("en"),
  theme: ThemeTokens,
  /** Scene preset id (must exist in the brand's `scenes` map). */
  scene: z.string().default("default"),
  /** Pack pins, e.g. "brickbrackets@^1". */
  packs: z.array(z.string().regex(/^[a-z0-9-]+(@.+)?$/)),
  catalog: BrandCatalog.prefault({}),
  features: BrandFeatures.prefault({}),
  fitTags: BrandFitTags.prefault({}),
  /** UI copy overrides keyed by string id (see `copyKeys`). */
  copy: z.record(z.string(), z.string()).default({}),
  /** Where the shell fetches the registry. Defaults to the shared API. */
  apiBaseUrl: z.string().optional(),
  auth: z
    .object({
      authority: z.string(),
      clientId: z.string(),
      /** Cognito hosted-UI domain, e.g. https://auth.example.com */
      domain: z.string().optional(),
    })
    .optional(),
  analyticsKey: z.string().optional(),
});
export type BrandConfig = z.infer<typeof BrandConfig>;
export type BrandConfigInput = z.input<typeof BrandConfig>;

/* -------------------------------------------------------------------------- */
/* Scene preset: how the model is lit and shaded. Code, not JSON.             */
/* -------------------------------------------------------------------------- */

export interface MaterialContext {
  three: typeof THREE;
  /** Brand model colour token, already resolved. */
  color: string;
  /** Preview vs export-quality mesh (export meshes render the same way). */
  lod: "preview" | "export";
}

export interface LightSpec {
  intensity: number;
  position: [number, number, number];
  color?: string;
  castShadow?: boolean;
}

/** An emissive panel in the environment map. Positions are world space (Z up); only direction and size matter. */
export interface LightformerSpec {
  form?: "rect" | "circle" | "ring";
  intensity: number;
  color?: string;
  position: [number, number, number];
  /** Uniform size, or [width, height]. */
  scale: number | [number, number];
  /** Point the panel faces. Default the origin. */
  target?: [number, number, number];
}

export interface ScenePreset {
  id: string;
  /** Build the model material. Return any three.js material, including ShaderMaterial with custom GLSL. */
  material: (ctx: MaterialContext) => THREE.Material;
  environment?: {
    /** drei Environment preset name, or an HDRI url. */
    preset?: "studio" | "city" | "warehouse" | "apartment" | "sunset" | "dawn" | "night" | "forest" | "lobby" | "park";
    hdri?: string;
    /** Reflection/ambient strength of the environment map. Default 1. */
    intensity?: number;
    /** Draw the environment as background too. */
    background?: boolean;
    /**
     * Procedural studio: emissive panels rendered into the environment map
     * (on top of `preset`/`hdri` when given, alone otherwise). No network
     * fetch, and the panels show up as crisp highlights on glossy materials.
     */
    lightformers?: LightformerSpec[];
  };
  lights: {
    ambient?: number;
    hemisphere?: { sky: string; ground: string; intensity: number };
    key?: LightSpec;
    fill?: LightSpec;
    rim?: LightSpec;
  };
  /** CSS colour, "transparent", or a two-stop vertical gradient. */
  background: string | { gradient: [string, string] };
  grid?: {
    show: boolean;
    cellSize?: number;
    sectionSize?: number;
    color?: string;
    sectionColor?: string;
    fadeDistance?: number;
    infinite?: boolean;
  };
  shadows?: boolean;
  toneMapping?: "ACES" | "Neutral" | "None";
  exposure?: number;
  camera?: {
    fov?: number;
    /** Multiplier on the auto-fit distance. */
    distanceFactor?: number;
    /** Auto-rotate speed in the catalog cards and idle viewport; 0 disables. */
    autoRotate?: number;
  };
  /** Optional post-processing hook: receives the R3F root state; return cleanup. */
  effects?: ComponentType;
}

/* -------------------------------------------------------------------------- */
/* Slot components and widget overrides                                        */
/* -------------------------------------------------------------------------- */

export interface CatalogEntryView {
  id: string;
  version: string;
  title: string;
  description?: string;
  tags: string[];
  thumbnailUrl?: string;
  href: string;
}

export interface HeaderProps {
  brand: BrandConfig;
  user?: { name?: string; email?: string; picture?: string } | null;
  onSignIn?: () => void;
  onSignOut?: () => void;
  nav: { label: string; href: string; active?: boolean }[];
}

export interface BrandSlots {
  Header: ComponentType<HeaderProps>;
  Footer: ComponentType<{ brand: BrandConfig }>;
  CatalogCard: ComponentType<{ entry: CatalogEntryView; children?: ReactNode }>;
  EmptyState: ComponentType<{ title: string; body?: string; action?: ReactNode }>;
  Logo: ComponentType<{ brand: BrandConfig; size?: number }>;
  /** Wraps the configurator page; brands use it to add side content around the panel. */
  ConfiguratorFrame: ComponentType<{ children: ReactNode; aside?: ReactNode }>;
}

export interface ParamWidgetProps<T = unknown> {
  spec: {
    name: string;
    label: string;
    type: "number" | "int" | "bool" | "select" | "string";
    min?: number;
    max?: number;
    step?: number;
    unit?: string;
    options?: { value: string; label: string }[];
    description?: string;
  };
  value: T;
  onChange: (next: T) => void;
  disabled?: boolean;
  invalid?: string;
}
export type ParamWidget<T = unknown> = ComponentType<ParamWidgetProps<T>>;

/* -------------------------------------------------------------------------- */
/* The full brand object apps import through the @brand alias                 */
/* -------------------------------------------------------------------------- */

export interface Brand {
  config: BrandConfig;
  scenes: Record<string, ScenePreset>;
  slots?: Partial<BrandSlots>;
  widgets?: Record<string, ParamWidget>;
  /** Extra CSS appended after the tokens (overrides.css contents). */
  css?: string;
}

export interface BrandInput {
  config: BrandConfigInput;
  scenes: Record<string, ScenePreset>;
  slots?: Partial<BrandSlots>;
  widgets?: Record<string, ParamWidget>;
  css?: string;
}

export function defineBrand(input: BrandInput): Brand {
  const config = BrandConfig.parse(input.config);
  if (!input.scenes[config.scene]) {
    throw new Error(`Brand ${config.id}: scene preset "${config.scene}" is not defined (have: ${Object.keys(input.scenes).join(", ")})`);
  }
  return { config, scenes: input.scenes, slots: input.slots, widgets: input.widgets, css: input.css };
}

/** Deep-merge serializable overrides (brand.json) into a compiled brand config. */
export function mergeBrandConfig(base: BrandConfig, overrides: Partial<BrandConfigInput> | null | undefined): BrandConfig {
  if (!overrides) return base;
  const merged: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(overrides)) {
    if (v === undefined) continue;
    const cur = (merged as Record<string, unknown>)[k];
    if (v && typeof v === "object" && !Array.isArray(v) && cur && typeof cur === "object" && !Array.isArray(cur)) {
      merged[k] = deepMerge(cur as Record<string, unknown>, v as Record<string, unknown>);
    } else {
      merged[k] = v;
    }
  }
  return BrandConfig.parse(merged);
}

function deepMerge(a: Record<string, unknown>, b: Record<string, unknown>): Record<string, unknown> {
  const out = { ...a };
  for (const [k, v] of Object.entries(b)) {
    if (v && typeof v === "object" && !Array.isArray(v) && a[k] && typeof a[k] === "object" && !Array.isArray(a[k])) {
      out[k] = deepMerge(a[k] as Record<string, unknown>, v as Record<string, unknown>);
    } else if (v !== undefined) {
      out[k] = v;
    }
  }
  return out;
}

/** Copy ids the core UI looks up; brands override any subset in `config.copy`. */
export const copyDefaults = {
  "nav.catalog": "Designs",
  "nav.mine": "My designs",
  "catalog.title": "Pick a design",
  "catalog.empty": "No designs published yet.",
  "configurator.params": "Parameters",
  "configurator.presets": "Presets",
  "configurator.reset": "Reset",
  "configurator.export.stl": "Download STL",
  "configurator.export.3mf": "Download 3MF",
  "configurator.save": "Save",
  "configurator.saved": "Saved",
  "configurator.share": "Share link",
  "configurator.update": "Update to {version}",
  "configurator.building": "Building…",
  "configurator.warnings": "Print check",
  "mine.title": "My designs",
  "mine.empty": "Nothing saved yet. Configure a design and press Save.",
  "auth.signIn": "Sign in",
  "auth.signOut": "Sign out",
  "auth.required": "Sign in to save designs to your profile.",
  "footer.madeWith": "Geometry is generated in your browser; nothing is uploaded until you save.",
  "nav.community": "Community",
  "fits.add": "Add",
  "fits.invalid": "That doesn't look like a valid reference.",
  "fits.max": "Up to {max} tags.",
  "fits.printed": "I've printed this and it fits",
  "fits.notes": "Notes (fit, material, printer…)",
  "fits.chip": "Fits {label} {ref}",
  "community.title": "Community configurations",
  "community.sub": "Parameter sets other makers printed and tagged.",
  "community.search": "Search by reference",
  "community.allDesigns": "All designs",
  "community.empty": "Nothing published here yet.",
  "community.popular": "Popular",
  "community.more": "Load more",
  "community.open": "Open in configurator",
  "community.by": "by {name}",
  "community.printed": "Printed by author",
  "community.confirms": "{count} also printed it",
  "community.confirm": "I printed this too",
  "community.confirmed": "Thanks for confirming",
  "community.report": "Report",
  "community.reported": "Reported. Thanks, we'll take a look.",
  "community.publish": "Publish to community",
  "community.unpublish": "Make private",
  "community.public": "Public",
  "community.publishHint": "Save first, then publish so others can find it.",
  "profile.displayName": "Display name",
  "profile.prompt": "Pick a public display name. It's shown on what you publish; your email never is.",
  "profile.save": "Save name",
  "mod.title": "Moderation queue",
  "mod.empty": "No reported entries.",
} as const;
export type CopyKey = keyof typeof copyDefaults;

export function makeCopy(config: Pick<BrandConfig, "copy">) {
  return (key: CopyKey, vars: Record<string, string | number> = {}): string => {
    let s = config.copy[key] ?? copyDefaults[key] ?? key;
    for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v));
    return s;
  };
}

export type { ThemeTokensInput };
